import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface UseResizablePanelOptions {
  axis: 'horizontal' | 'vertical';
  /** Which edge of the PANEL the handle sits on: 'end' = trailing (right/bottom),
   *  so a positive pointer delta grows the panel; 'start' inverts the sign. */
  handleEdge: 'start' | 'end';
  /** The committed size; the returned `size` tracks the live drag over it. */
  size: number;
  onCommit: (size: number) => void;
  min: number;
  max: number;
}

export interface UseResizablePanelReturn {
  size: number;
  isResizing: boolean;
  onHandlePointerDown: (e: ReactPointerEvent) => void;
}

export function useResizablePanel({
  axis,
  handleEdge,
  size,
  onCommit,
  min,
  max,
}: UseResizablePanelOptions): UseResizablePanelReturn {
  const [liveSize, setLiveSize] = useState<number | null>(null);
  const liveRef = useRef<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!isResizing) return;
    document.body.style.cursor = axis === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, axis]);

  // Window-level pointermove/up listeners attached on pointerdown, never
  // `setPointerCapture` — capture silently drops synthetic/injected pointer
  // streams (Playwright); see ShogunPanelFrame's drag handlers.
  const onHandlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      const startCoord = axis === 'horizontal' ? e.clientX : e.clientY;
      const startSize = liveRef.current ?? size;
      const sign = handleEdge === 'end' ? 1 : -1;
      const onMove = (ev: PointerEvent) => {
        const coord = axis === 'horizontal' ? ev.clientX : ev.clientY;
        const next = Math.max(min, Math.min(max, startSize + sign * (coord - startCoord)));
        liveRef.current = next;
        setLiveSize(next);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setIsResizing(false);
        if (liveRef.current !== null) onCommit(liveRef.current);
        liveRef.current = null;
        setLiveSize(null);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      setIsResizing(true);
    },
    [axis, handleEdge, size, min, max, onCommit],
  );

  return { size: liveSize ?? size, isResizing, onHandlePointerDown };
}
